import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { usePageLanguage } from '@/hooks/usePageLanguage';
import { useJsonLd } from '@/hooks/useJsonLd';
import { SITE_ORIGIN } from '@/data/site';
import { MAIN_SITE_URL } from '@/data/categories';
import { getArticle, type GuideArticle, type LocalizedText, type GuideFaq } from '@/data/articles';

const PUBLISHED_DATE = '2026-08-19';
const UPDATED_DATE = '2026-08-19';

function pick(text: LocalizedText, lang: string): string {
  const key = lang as keyof LocalizedText;
  return text[key] ?? text.en;
}

export default function GuidePage() {
  const { lang = 'en', slug = '' } = useParams();
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const article: GuideArticle | undefined = getArticle(slug);

  usePageLanguage(lang);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useJsonLd(
    'guide-schema',
    article
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: pick(article.metaTitle, lang),
            description: pick(article.metaDescription, lang),
            author: { '@type': 'Organization', name: t('logo.name') },
            datePublished: PUBLISHED_DATE,
            dateModified: UPDATED_DATE,
            inLanguage: lang,
            mainEntityOfPage: `${SITE_ORIGIN}/${lang}/guide/${slug}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: t('logo.name'), item: `${SITE_ORIGIN}/${lang}` },
              { '@type': 'ListItem', position: 2, name: pick(article.metaTitle, lang), item: `${SITE_ORIGIN}/${lang}/guide/${slug}` },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: article.faqs.map((item: GuideFaq) => ({
              '@type': 'Question',
              name: pick(item.q, lang),
              acceptedAnswer: { '@type': 'Answer', text: pick(item.a, lang) },
            })),
          },
        ]
      : null,
  );

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-16">
          <div className="mx-auto max-w-3xl px-4 md:px-6 py-24 text-center">
            <p className="text-foreground-600 mb-6">{t('guide.notFound')}</p>
            <Link
              to={`/${lang}`}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 px-6 py-3 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('article.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16">
        <article className="mx-auto max-w-3xl px-4 md:px-6 py-10 md:py-16">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <li>
                <Link to={`/${lang}`} className="hover:text-primary-600 transition-colors cursor-pointer">
                  {t('logo.name')}
                </Link>
              </li>
              <li className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line text-sm" />
              </li>
              <li className="text-foreground-700 font-medium">{pick(article.h1, lang)}</li>
            </ol>
          </nav>

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="inline-flex rounded-full bg-accent-100 text-accent-800 text-xs font-semibold px-3 py-1">
                {pick(article.tag, lang)}
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-foreground-950 leading-tight mb-4">
              {pick(article.h1, lang)}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line text-sm" />
                </span>
                {t('article.author')}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm" />
                </span>
                {t('article.published')} {PUBLISHED_DATE}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line text-sm" />
                </span>
                {t('article.updated')} {UPDATED_DATE}
              </span>
            </div>
          </header>

          <div className="w-full h-56 md:h-72 rounded-lg overflow-hidden mb-8">
            <img
              src={article.image}
              alt={pick(article.h1, lang)}
              title={pick(article.h1, lang)}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-10">
            {pick(article.intro, lang)}
          </p>

          {article.sections.map((section, i) => (
            <section key={i} className="mb-10">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-4">
                {pick(section.heading, lang)}
              </h2>
              <p className="text-foreground-700 leading-relaxed">{pick(section.body, lang)}</p>
            </section>
          ))}

          <div className="rounded-lg bg-primary-50 border border-primary-200 p-8 text-center mb-10">
            <h2 className="font-heading font-bold text-xl text-foreground-950 mb-2">
              {t('article.ctaTitle')}
            </h2>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 px-6 py-3 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap mt-4"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-external-link-line text-base" />
              </span>
              {t('article.ctaButton')}
            </a>
          </div>

          <section className="mb-10">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">
              {t('article.faqTitle')}
            </h2>
            <div className="space-y-3">
              {article.faqs.map((item, i) => {
                const isOpen = openFaq === i;
                return (
                  <div
                    key={i}
                    className="rounded-lg border border-background-200 bg-background-50 overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer"
                      aria-expanded={isOpen}
                    >
                      <span className="font-heading font-semibold text-sm md:text-base text-foreground-900">
                        {pick(item.q, lang)}
                      </span>
                      <span className="w-6 h-6 flex items-center justify-center shrink-0 text-primary-600">
                        <i className={`text-lg transition-transform ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`} />
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-5 pb-5">
                        <p className="text-sm text-foreground-600 leading-relaxed">{pick(item.a, lang)}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          <div className="border-t border-background-200 pt-6">
            <Link
              to={`/${lang}`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-base" />
              </span>
              {t('article.backHome')}
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}